// Global
import React, { JSX } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Content } from '.generated/Feature.CW.model';
import { withStandardComponentWrapper } from 'helpers/HOC';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import useIsEditing from 'lib/hooks/useIsEditing';

export type HeroProps = Content.Hero_Component;

const Hero = (props: HeroProps): JSX.Element => {
  const { cta1Link, cta2Link, Description, Heading, Image } = props?.fields || {};
  const isEditing = useIsEditing();

  const styles = parseStyleParams(props.params, ['cta1', 'cta2']);

  const { base, cta, columnA, columnB, contentContainer, ctaContainer, description, heading } =
    TAILWIND_VARIANTS();

  return (
    <section
      className={(base(), 'theme-lightblue')}
      data-component="authorable/shared/content/hero"
    >
      <div className={columnA()}>
        <div className={contentContainer()}>
          {(Heading?.value !== '' || isEditing) && (
            <h2 className={(heading(), 'font-centuryGothic font-bold')}>
              <PlainTextWrapper field={Heading} />
            </h2>
          )}

          <span className="font-nunitoSans">Test Fonts Text</span>
          <RichTextWrapper className={description()} field={Description} tag="div" />
          <div className={ctaContainer()}>
            <LinkWrapper
              className={cta({ style: styles.cta1?.ctaVariant })}
              ctaStyle={getCtaStyle(styles.cta1, 'primary')}
              field={cta1Link}
              suppressNewTabIcon={true}
            />
            <LinkWrapper
              className={cta({ style: styles.cta2?.ctaVariant })}
              ctaStyle={getCtaStyle(styles.cta2, 'secondary')}
              suppressNewTabIcon={true}
              field={cta2Link}
            />
          </div>
        </div>
      </div>
      <div className={columnB()}>
        <ImageWrapper field={Image} priority={true} />
      </div>
    </section>
  );
};

export const Default = withStandardComponentWrapper(Hero);

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    base: ['flex', 'flex-col-reverse', 'items-center', 'md:flex-row'],
    cta: [],
    columnA: ['flex', 'w-full', 'md:w-1/2'],
    columnB: ['w-full', 'md:w-1/2'],
    contentContainer: ['md:max-w-lg', 'p-4', 'w-full'],
    ctaContainer: ['flex', 'flex-wrap', 'gap-6', 'md:justify-normal'],
    description: ['mb-6', 'text-base'],
    heading: ['font-bold', 'font-modern', 'mb-6', 'text-5xl', 'md:text-4xl'],
  },
  variants: {
    style: {
      link: {
        cta: ['text-base', 'text-theme-brand3'],
      },
      primary: {
        cta: ['px-8'],
      },
      secondary: {
        cta: ['px-8'],
      },
      secondarySmall: {
        cta: ['px-8'],
      },
      tertiary: {
        cta: ['px-8'],
      },
      hyperlink: {
        cta: ['px-8'],
      },
      jumplink: {
        cta: ['px-8'],
      },
    },
  },
});
